import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ErrorView from '../../pages/ErrorView'
import SydButton from '../../commonDesign/Button'
import { ICON_NAMES } from '../../../constants'
import AuthDialog from '../../pages/preferences/integrations/AuthDialog'
import { IntegrationAuthContext } from './IntegrationAuthContext'

const SIZE_CONFIG = {
  sm: {
    titleClass: 'font-size-lg',
    subtitleClass: 'font-size-sm',
    buttonSize: 'md',
    gap: 'gap-1'
  },
  md: {
    titleClass: 'font-size-lg',
    subtitleClass: 'font-size-md',
    buttonSize: 'md',
    gap: 'gap-2'
  },
  lg: {
    titleClass: 'font-size-2xl',
    subtitleClass: 'font-size-lg',
    buttonSize: 'lg',
    gap: 'gap-3'
  }
}

function IntegrationAuthGate ({
  authState,
  children,
  allowAuth = true,
  size = 'md',
  authLabels = {
    connectButton: 'Connect',
    buttonIcon: ICON_NAMES.link,
    title: 'Authentication Required',
    subtitle: ''
  },
  errorLabels = {
    title: 'Error loading authentication',
    subtitle: 'An error occurred while checking authentication status. Please try again later.'
  },
  authStyles = {},
  authClasses = {},
  width = '100%',
  height = '100%'
}) {
  const {
    isAuthenticated,
    requiresAuth,
    error,
    authInputs,
    domainName,
    contextValue,
    authDialogOpen,
    setAuthDialogOpen,
    handleConnectClick,
    handleAuthSuccess,
    integration
  } = authState

  const dialogRef = useRef(null)
  const sizeConfig = SIZE_CONFIG[size] || SIZE_CONFIG.md

  useEffect(() => {
    if (authDialogOpen && dialogRef.current && integration && authInputs) {
      dialogRef.current.open(integration, authInputs, { domainName })
      setAuthDialogOpen(false)
    }
  }, [authDialogOpen, integration, authInputs, domainName, setAuthDialogOpen])

  const onComplete = useCallback(() => {
    handleAuthSuccess()
  }, [handleAuthSuccess])

  if (requiresAuth && allowAuth) {
    return (
      <div
        className={`flex flex-col items-center justify-center ${authClasses.container || ''}`}
        style={{ width, height, ...authStyles.container }}
      >
        <div className={`flex flex-col items-center justify-center ${sizeConfig.gap} ${authClasses.content || ''}`} style={authStyles.content}>
          <p
            className={`${sizeConfig.titleClass} text-center my-0 ${authClasses.title || ''}`}
            style={authStyles.title}
          >
            {authLabels.title}
          </p>
          {authLabels.subtitle && (
            <p
              className={`${sizeConfig.subtitleClass} text-center mt-0 mb-4 ${authClasses.subtitle || ''}`}
              style={authStyles.subtitle}
            >
              {authLabels.subtitle}
            </p>
          )}
          <SydButton
            size={sizeConfig.buttonSize}
            variant='primary'
            onClick={handleConnectClick}
            icon={authLabels.buttonIcon}
            className={authClasses.button}
            style={authStyles.button}
            disabled={!authInputs}
          >
            {authLabels.connectButton}
          </SydButton>
        </div>
        <AuthDialog ref={dialogRef} onComplete={onComplete} />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorView
        width='100%'
        height='auto'
        error={{
          message: errorLabels.title,
          details: errorLabels.subtitle
        }}
      />
    )
  }

  if (!isAuthenticated) {
    return (
      <ErrorView
        width='100%'
        height='auto'
        error={{
          message: 'Unable to verify authentication status',
          details: 'Please try again later.'
        }}
      />
    )
  }

  return (
    <IntegrationAuthContext.Provider value={contextValue}>
      {children}
    </IntegrationAuthContext.Provider>
  )
}

IntegrationAuthGate.propTypes = {
  authState: PropTypes.object.isRequired,
  children: PropTypes.node,
  allowAuth: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  authLabels: PropTypes.shape({
    connectButton: PropTypes.string,
    buttonIcon: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  errorLabels: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  authStyles: PropTypes.shape({
    container: PropTypes.object,
    content: PropTypes.object,
    title: PropTypes.object,
    subtitle: PropTypes.object,
    button: PropTypes.object
  }),
  authClasses: PropTypes.shape({
    container: PropTypes.string,
    content: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    button: PropTypes.string
  }),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default IntegrationAuthGate
